/**
 * Test script for attack animation synchronization.
 * 
 * This script simulates a player attack and logs the animation process.
 * To use this script, call the testAttackAnimation function from the browser console.
 */

/**
 * Test the attack animation synchronization
 * @param {string} characterType - The character type to test (BARBARIAN, KNIGHT, MAGE, ROGUE)
 * @param {boolean} fullCycle - Whether to test the full attack cycle including ending
 */
function testAttackAnimation(characterType = 'BARBARIAN', fullCycle = true) {
  console.log(`==== Testing attack animation for ${characterType} ====`);
  
  // Get the global game object and remote players manager
  if (!window.game) {
    console.error('Game object not found in window. Make sure the game is initialized.');
    return;
  }
  
  const player = window.game.player;
  if (!player) {
    console.error('Player not found in game object. Make sure player is initialized.');
    return;
  }
  
  console.log('Triggering attack...');
  
  // Map of character types to their attack animations
  const attackAnimations = {
    'BARBARIAN': '2H_Melee_Attack_Spinning',
    'KNIGHT': '2H_Melee_Attack_Chop',
    'MAGE': 'Spellcast_Shoot',
    'ROGUE': '2H_Ranged_Shoot'
  };
  
  // Set the player as attacking
  player.isAttacking = true;
  
  // Get attack animation based on character type
  const attackAnimation = attackAnimations[characterType] || 'attack';
  
  // Manually create a state update
  const playerState = {
    id: 'local-test',
    position: { 
      x: player.position.x, 
      y: player.position.y, 
      z: player.position.z 
    },
    rotation: { y: player.rotation.y },
    characterType: characterType,
    isMoving: false,
    isAttacking: true,
    attackType: attackAnimation,
    username: 'Test Player'
  };
  
  console.log('Player state for attack:', playerState);
  
  // List available animations in the player
  if (player.animation && player.animation.actions) {
    console.log('Available animations:');
    Object.keys(player.animation.actions).forEach(anim => {
      console.log(` - ${anim}`);
    });
    
    // Get animation duration
    const attackAction = player.animation.actions[attackAnimation];
    if (attackAction && attackAction.getClip) {
      const duration = attackAction.getClip().duration;
      console.log(`Attack animation duration: ${duration.toFixed(2)} seconds`);
    }
  } else {
    console.warn('No animations found on player');
  }
  
  // Try to play the animation
  console.log(`Playing animation: ${attackAnimation}`);
  player.playAnimation(attackAnimation);

  // Log animation progress
  let progressInterval;
  if (fullCycle) {
    let startTime = Date.now();
    
    progressInterval = setInterval(() => {
      const elapsedTime = (Date.now() - startTime) / 1000;
      const attackAction = player.animation && player.animation.actions && 
                          player.animation.actions[attackAnimation];
      
      if (attackAction && typeof attackAction.time !== 'undefined') {
        const clipDuration = attackAction.getClip ? attackAction.getClip().duration : 1.5;
        const progress = (attackAction.time / clipDuration) * 100;
        console.log(`Animation progress: ${progress.toFixed(2)}% (Elapsed: ${elapsedTime.toFixed(2)}s)`);
      } else {
        console.log(`Elapsed time: ${elapsedTime.toFixed(2)}s`);
      }
    }, 200); // Report progress every 200ms
  }
  
  // Simulate ending the attack after animation duration
  if (fullCycle) {
    // Get the appropriate animation duration based on character
    const animationDurations = {
      'BARBARIAN': 1.8,  // Duration of Barbarian's spinning attack
      'KNIGHT': 1.2,     // Duration of Knight's chop attack
      'MAGE': 2.0,       // Duration of Mage's spell cast
      'ROGUE': 1.5       // Duration of Rogue's shoot animation
    };
    
    // Get duration or use default
    const duration = animationDurations[characterType] || 1.5;
    
    // Add small buffer to ensure animation completes
    const bufferTime = 0.2;
    const totalDuration = (duration + bufferTime) * 1000;
    
    console.log(`Attack animation will end automatically after ${totalDuration}ms`);
    
    setTimeout(() => {
      console.log('Animation should have completed, ending attack state...');
      player.isAttacking = false;
      
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // Reset to idle animation
      player.playAnimation('idle');
      
      console.log('Attack animation test complete - transitioned back to idle');
    }, totalDuration);
    
    return `Attack animation test initiated. Will run for ${totalDuration}ms. Check console for results.`;
  } else {
    console.log('Test only started the animation. Will not auto-complete.');
    return 'Attack animation test initiated without auto-complete. Check console for results.';
  }
}

/**
 * Test attack animations for all character types in sequence
 */
function testAllAttackAnimations() {
  console.log('Testing all character attack animations in sequence...');
  
  const characters = ['BARBARIAN', 'KNIGHT', 'MAGE', 'ROGUE'];
  let index = 0;
  
  function testNext() {
    if (index < characters.length) {
      const character = characters[index];
      console.log(`\n\n========== Testing ${character} attack (${index + 1}/${characters.length}) ==========\n\n`);
      
      // Test with the current character
      testAttackAnimation(character, true);
      
      // Move to the next character after delay
      index++;
      setTimeout(testNext, 3000); // 3 second delay between tests
    } else {
      console.log('\n\nAll character attack animations tested!');
    }
  }
  
  // Start testing
  testNext();
  
  return 'Testing all character attack animations in sequence...';
}

// Make the functions available globally for testing from the console
window.testAttackAnimation = testAttackAnimation;
window.testAllAttackAnimations = testAllAttackAnimations;

console.log('Attack animation test script loaded. Call testAttackAnimation() or testAllAttackAnimations() to test.');

export { testAttackAnimation, testAllAttackAnimations }; 